import { useState, useEffect } from 'react';
import { useMutation, useSubscription } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { Button } from '../ui/button';
import toast from 'react-hot-toast';

// Type definitions for GraphQL responses
interface WaitingParticipant {
  id: string;
  name: string;
  participantType: 'CLIENT' | 'INQUIRY' | 'GUEST' | 'STAFF';
  requestedAt: string;
}

interface VideoConferenceWithWaitingRoom {
  id: string;
  waitingRoomParticipants: WaitingParticipant[];
  waitingRoomEnabled?: boolean;
}

interface AdmitParticipantData {
  admitParticipant: VideoConferenceWithWaitingRoom;
}

interface DenyParticipantData {
  denyParticipant: VideoConferenceWithWaitingRoom;
}

interface AdmitAllParticipantsData {
  admitAllParticipants: VideoConferenceWithWaitingRoom;
}

interface ToggleWaitingRoomData {
  toggleWaitingRoom: {
    id: string;
    waitingRoomEnabled: boolean;
  };
}

interface WaitingRoomSubscriptionData {
  onWaitingRoomUpdated: VideoConferenceWithWaitingRoom;
}

const ADMIT_PARTICIPANT = gql`
  mutation AdmitParticipant($conferenceId: ID!, $participantId: ID!) {
    admitParticipant(conferenceId: $conferenceId, participantId: $participantId) {
      id
      waitingRoomParticipants {
        id
        name
        participantType
        requestedAt
      }
    }
  }
`;

const DENY_PARTICIPANT = gql`
  mutation DenyParticipant($conferenceId: ID!, $participantId: ID!) {
    denyParticipant(conferenceId: $conferenceId, participantId: $participantId) {
      id
      waitingRoomParticipants {
        id
        name
        participantType
        requestedAt
      }
    }
  }
`;

const ADMIT_ALL_PARTICIPANTS = gql`
  mutation AdmitAllParticipants($conferenceId: ID!) {
    admitAllParticipants(conferenceId: $conferenceId) {
      id
      waitingRoomParticipants {
        id
        name
        participantType
        requestedAt
      }
    }
  }
`;

const TOGGLE_WAITING_ROOM = gql`
  mutation ToggleWaitingRoom($conferenceId: ID!, $enabled: Boolean!) {
    toggleWaitingRoom(conferenceId: $conferenceId, enabled: $enabled) {
      id
      waitingRoomEnabled
    }
  }
`;

const ON_WAITING_ROOM_UPDATED = gql`
  subscription OnWaitingRoomUpdated($conferenceId: ID!) {
    onWaitingRoomUpdated(conferenceId: $conferenceId) {
      id
      waitingRoomParticipants {
        id
        name
        participantType
        requestedAt
      }
    }
  }
`;

interface WaitingRoomPanelProps {
  conferenceId: string;
  waitingParticipants: WaitingParticipant[];
  waitingRoomEnabled: boolean;
  onParticipantsChange?: (participants: WaitingParticipant[]) => void;
}

export function WaitingRoomPanel({
  conferenceId,
  waitingParticipants: initialParticipants,
  waitingRoomEnabled: initialEnabled,
  onParticipantsChange,
}: WaitingRoomPanelProps) {
  const [participants, setParticipants] = useState<WaitingParticipant[]>(initialParticipants);
  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState(initialEnabled);

  const [admitParticipantMutation, { loading: admitting }] = useMutation<AdmitParticipantData>(ADMIT_PARTICIPANT);
  const [denyParticipantMutation, { loading: denying }] = useMutation<DenyParticipantData>(DENY_PARTICIPANT);
  const [admitAllMutation, { loading: admittingAll }] = useMutation<AdmitAllParticipantsData>(ADMIT_ALL_PARTICIPANTS);
  const [toggleWaitingRoomMutation, { loading: toggling }] = useMutation<ToggleWaitingRoomData>(TOGGLE_WAITING_ROOM);

  // Subscribe to waiting room updates
  useSubscription<WaitingRoomSubscriptionData>(ON_WAITING_ROOM_UPDATED, {
    variables: { conferenceId },
    onData: ({ data }) => {
      if (data?.data?.onWaitingRoomUpdated?.waitingRoomParticipants) {
        const newParticipants = data.data.onWaitingRoomUpdated.waitingRoomParticipants;
        setParticipants(newParticipants);
        onParticipantsChange?.(newParticipants);
      }
    },
  });

  // Update local state when props change
  useEffect(() => {
    setParticipants(initialParticipants);
  }, [initialParticipants]);

  useEffect(() => {
    setWaitingRoomEnabled(initialEnabled);
  }, [initialEnabled]);

  const handleAdmit = async (participantId: string, participantName: string) => {
    try {
      const result = await admitParticipantMutation({
        variables: { conferenceId, participantId },
      });
      const newParticipants = result.data?.admitParticipant?.waitingRoomParticipants || [];
      setParticipants(newParticipants);
      onParticipantsChange?.(newParticipants);
      toast.success(`${participantName} admitted to the call`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to admit participant');
    }
  };

  const handleDeny = async (participantId: string, participantName: string) => {
    try {
      const result = await denyParticipantMutation({
        variables: { conferenceId, participantId },
      });
      const newParticipants = result.data?.denyParticipant?.waitingRoomParticipants || [];
      setParticipants(newParticipants);
      onParticipantsChange?.(newParticipants);
      toast.success(`${participantName} denied entry`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to deny participant');
    }
  };

  const handleAdmitAll = async () => {
    try {
      const result = await admitAllMutation({
        variables: { conferenceId },
      });
      const newParticipants = result.data?.admitAllParticipants?.waitingRoomParticipants || [];
      setParticipants(newParticipants);
      onParticipantsChange?.(newParticipants);
      toast.success('All participants admitted');
    } catch (error: any) {
      toast.error(error.message || 'Failed to admit all participants');
    }
  };

  const handleToggleWaitingRoom = async () => {
    try {
      const result = await toggleWaitingRoomMutation({
        variables: { conferenceId, enabled: !waitingRoomEnabled },
      });
      const newEnabled = result.data?.toggleWaitingRoom?.waitingRoomEnabled ?? !waitingRoomEnabled;
      setWaitingRoomEnabled(newEnabled);
      toast.success(newEnabled ? 'Waiting room enabled' : 'Waiting room disabled');
    } catch (error: any) {
      toast.error(error.message || 'Failed to toggle waiting room');
    }
  };

  const getParticipantTypeColor = (type: string): string => {
    const colors: Record<string, string> = {
      CLIENT: 'bg-blue-100 text-blue-700',
      INQUIRY: 'bg-purple-100 text-purple-700',
      GUEST: 'bg-orange-100 text-orange-700',
      STAFF: 'bg-green-100 text-green-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const formatWaitTime = (requestedAt: string): string => {
    const now = new Date();
    const requested = new Date(requestedAt);
    const diffMs = now.getTime() - requested.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours === 1) return '1 hour ago';
    return `${diffHours} hours ago`;
  };

  const isLoading = admitting || denying || admittingAll || toggling;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-aalaw-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="font-medium text-gray-900">
            Waiting Room ({participants.length})
          </span>
        </div>
        
        {/* Toggle Switch */}
        <button
          onClick={handleToggleWaitingRoom}
          disabled={toggling}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            waitingRoomEnabled ? 'bg-aalaw-red' : 'bg-gray-300'
          } ${toggling ? 'opacity-50 cursor-not-allowed' : ''}`}
          title={waitingRoomEnabled ? 'Disable waiting room' : 'Enable waiting room'}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              waitingRoomEnabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      {/* Content */}
      <div className="p-3">
        {!waitingRoomEnabled ? (
          <p className="text-sm text-gray-500 text-center py-4">
            Waiting room is disabled. Participants will join directly.
          </p>
        ) : participants.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-4">
            No one is waiting to join.
          </p>
        ) : (
          <>
            {/* Participant List */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {participants.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    {/* Avatar */}
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-sm font-medium flex-shrink-0">
                      {participant.name.charAt(0).toUpperCase()}
                    </div>
                    
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {participant.name}
                      </p>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${getParticipantTypeColor(participant.participantType)}`}>
                          {participant.participantType}
                        </span>
                        <span className="text-xs text-gray-400">
                          {formatWaitTime(participant.requestedAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1 ml-2">
                    <button
                      onClick={() => handleAdmit(participant.id, participant.name)}
                      disabled={isLoading}
                      className="p-1.5 text-green-600 hover:bg-green-100 rounded-full transition-colors disabled:opacity-50"
                      title="Admit"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeny(participant.id, participant.name)}
                      disabled={isLoading}
                      className="p-1.5 text-red-600 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50"
                      title="Deny"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Admit All Button */}
            {participants.length > 1 && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleAdmitAll}
                  disabled={isLoading}
                  className="w-full"
                >
                  Admit All ({participants.length})
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
